/**
 * Transaction Service - Handles merchant transaction operations
 * Provides mock transaction data for demonstration purposes
 */

class TransactionService {
  constructor() {
    this.storagePrefix = 'merchant_transactions_';
  }

  /**
   * Get merchant-specific storage key
   */
  getMerchantTransactionKey(merchantId) {
    return `${this.storagePrefix}${merchantId}`;
  }

  /**
   * Generate mock transaction data for a merchant
   */
  generateMockTransactions(merchantId, count = 20) {
    const transactions = [];
    const statuses = ['SUCCESS', 'PENDING', 'FAILED', 'REFUNDED'];
    const paymentMethods = ['UPI', 'CARD', 'NET_BANKING', 'WALLET'];
    const currencies = ['INR'];
    
    for (let i = 0; i < count; i++) {
      const status = statuses[Math.floor(Math.random() * statuses.length)];
      const amount = Math.floor(Math.random() * 50000) + 100; // ₹100 to ₹50,000
      const createdAt = new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000); // Last 30 days
      
      transactions.push({
        id: `txn_${merchantId}_${Date.now()}_${i}`,
        merchantId,
        orderId: `order_${Date.now()}_${i}`,
        amount,
        currency: 'INR',
        status,
        paymentMethod: paymentMethods[Math.floor(Math.random() * paymentMethods.length)],
        customerEmail: `customer${i}@example.com`,
        customerPhone: `+91${Math.floor(Math.random() * 9000000000) + 1000000000}`,
        description: `Payment for Order #${1000 + i}`,
        createdAt: createdAt.toISOString(),
        updatedAt: createdAt.toISOString(),
        fees: Math.floor(amount * 0.02), // 2% fees
        netAmount: amount - Math.floor(amount * 0.02),
        gateway: 'Razorpay',
        gatewayTransactionId: `pay_${Math.random().toString(36).substr(2, 9)}`,
        refundAmount: status === 'REFUNDED' ? amount : 0,
        metadata: {
          userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          ipAddress: `192.168.1.${Math.floor(Math.random() * 255)}`,
          source: 'web'
        }
      });
    }
    
    // Sort by creation date (newest first)
    return transactions.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }

  /**
   * Get transactions for a merchant
   */
  getMerchantTransactions(merchantId) {
    try {
      const transactionKey = this.getMerchantTransactionKey(merchantId);
      let transactions = localStorage.getItem(transactionKey);
      
      if (!transactions) {
        // Generate mock transactions if none exist
        transactions = this.generateMockTransactions(merchantId);
        localStorage.setItem(transactionKey, JSON.stringify(transactions));
      } else {
        transactions = JSON.parse(transactions);
      }
      
      return transactions;
    } catch (error) {
      console.error('Error getting merchant transactions:', error);
      return [];
    }
  }

  /**
   * Get transaction statistics for a merchant
   */
  getMerchantTransactionStats(merchantId) {
    try {
      const transactions = this.getMerchantTransactions(merchantId);
      
      const stats = {
        totalTransactions: transactions.length,
        successfulTransactions: transactions.filter(t => t.status === 'SUCCESS').length,
        pendingTransactions: transactions.filter(t => t.status === 'PENDING').length,
        failedTransactions: transactions.filter(t => t.status === 'FAILED').length,
        refundedTransactions: transactions.filter(t => t.status === 'REFUNDED').length,
        totalAmount: transactions.reduce((sum, t) => sum + (t.status === 'SUCCESS' ? t.amount : 0), 0),
        totalFees: transactions.reduce((sum, t) => sum + (t.status === 'SUCCESS' ? t.fees : 0), 0),
        totalRefunds: transactions.reduce((sum, t) => sum + t.refundAmount, 0),
        averageTransactionAmount: 0,
        successRate: 0
      };
      
      if (stats.totalTransactions > 0) {
        stats.averageTransactionAmount = Math.round(stats.totalAmount / stats.successfulTransactions) || 0;
        stats.successRate = Math.round((stats.successfulTransactions / stats.totalTransactions) * 100);
      }
      
      return stats;
    } catch (error) {
      console.error('Error calculating transaction stats:', error);
      return {
        totalTransactions: 0,
        successfulTransactions: 0,
        pendingTransactions: 0,
        failedTransactions: 0,
        refundedTransactions: 0,
        totalAmount: 0,
        totalFees: 0,
        totalRefunds: 0,
        averageTransactionAmount: 0,
        successRate: 0
      };
    }
  }

  /**
   * Get all merchants' transaction statistics (for admin)
   */
  getAllMerchantsTransactionStats() {
    try {
      const allStats = {
        totalTransactions: 0,
        successfulTransactions: 0,
        pendingTransactions: 0,
        failedTransactions: 0,
        refundedTransactions: 0,
        totalAmount: 0,
        totalFees: 0,
        totalRefunds: 0,
        totalMerchants: 0
      };

      // Get all merchant transaction keys from localStorage
      const merchantIds = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.storagePrefix)) {
          const merchantId = key.replace(this.storagePrefix, '');
          merchantIds.push(merchantId);
        }
      }

      allStats.totalMerchants = merchantIds.length;

      // Aggregate stats from all merchants
      merchantIds.forEach(merchantId => {
        const merchantStats = this.getMerchantTransactionStats(merchantId);
        allStats.totalTransactions += merchantStats.totalTransactions;
        allStats.successfulTransactions += merchantStats.successfulTransactions;
        allStats.pendingTransactions += merchantStats.pendingTransactions;
        allStats.failedTransactions += merchantStats.failedTransactions;
        allStats.refundedTransactions += merchantStats.refundedTransactions;
        allStats.totalAmount += merchantStats.totalAmount;
        allStats.totalFees += merchantStats.totalFees;
        allStats.totalRefunds += merchantStats.totalRefunds;
      });

      return allStats;
    } catch (error) {
      console.error('Error getting all merchants transaction stats:', error);
      return {
        totalTransactions: 0,
        successfulTransactions: 0,
        pendingTransactions: 0,
        failedTransactions: 0,
        refundedTransactions: 0,
        totalAmount: 0,
        totalFees: 0,
        totalRefunds: 0,
        totalMerchants: 0
      };
    }
  }

  /**
   * Search transactions by various criteria
   */
  searchTransactions(merchantId, filters = {}) {
    try {
      console.log('🔍 Searching transactions with filters:', filters);
      let transactions = this.getMerchantTransactions(merchantId);
      console.log('📊 Initial transactions count:', transactions.length);
      
      if (transactions.length === 0) {
        console.log('⚠️ No transactions found for merchant:', merchantId);
        return [];
      }
      
      // Log sample transaction structure for debugging
      if (transactions.length > 0) {
        console.log('📋 Sample transaction structure:', {
          id: transactions[0].id,
          createdAt: transactions[0].createdAt,
          status: transactions[0].status,
          paymentMethod: transactions[0].paymentMethod,
          amount: transactions[0].amount
        });
      }
      
      // Apply time period filter first
      if (filters.timePeriod && filters.timePeriod !== '') {
        const dateRange = this.getDateRangeForPeriod(filters.timePeriod);
        console.log('📅 Date range for period:', filters.timePeriod, dateRange);
        if (dateRange) {
          const beforeCount = transactions.length;
          transactions = transactions.filter(t => {
            const transactionDate = new Date(t.createdAt);
            const isInRange = transactionDate >= dateRange.start && transactionDate <= dateRange.end;
            if (!isInRange && beforeCount <= 5) {
              console.log(`📅 Transaction ${t.id} (${transactionDate}) not in range ${dateRange.start} - ${dateRange.end}`);
            }
            return isInRange;
          });
          console.log(`📅 After time filter (${filters.timePeriod}): ${beforeCount} → ${transactions.length}`);
        }
      }
      
      // Apply other filters
      if (filters.status && filters.status !== '') {
        const beforeCount = transactions.length;
        transactions = transactions.filter(t => t.status === filters.status);
        console.log(`🔄 After status filter (${filters.status}): ${beforeCount} → ${transactions.length}`);
      }
      
      if (filters.paymentMethod && filters.paymentMethod !== '') {
        const beforeCount = transactions.length;
        transactions = transactions.filter(t => t.paymentMethod === filters.paymentMethod);
        console.log(`💳 After payment method filter (${filters.paymentMethod}): ${beforeCount} → ${transactions.length}`);
      }
      
      if (filters.dateFrom) {
        const beforeCount = transactions.length;
        transactions = transactions.filter(t => new Date(t.createdAt) >= new Date(filters.dateFrom));
        console.log(`📅 After dateFrom filter: ${beforeCount} → ${transactions.length}`);
      }
      
      if (filters.dateTo) {
        const beforeCount = transactions.length;
        transactions = transactions.filter(t => new Date(t.createdAt) <= new Date(filters.dateTo));
        console.log(`📅 After dateTo filter: ${beforeCount} → ${transactions.length}`);
      }
      
      if (filters.minAmount) {
        const beforeCount = transactions.length;
        transactions = transactions.filter(t => t.amount >= filters.minAmount);
        console.log(`💰 After minAmount filter: ${beforeCount} → ${transactions.length}`);
      }
      
      if (filters.maxAmount) {
        const beforeCount = transactions.length;
        transactions = transactions.filter(t => t.amount <= filters.maxAmount);
        console.log(`💰 After maxAmount filter: ${beforeCount} → ${transactions.length}`);
      }
      
      if (filters.searchTerm && filters.searchTerm.trim() !== '') {
        const beforeCount = transactions.length;
        const term = filters.searchTerm.toLowerCase().trim();
        transactions = transactions.filter(t => 
          t.orderId.toLowerCase().includes(term) ||
          t.customerEmail.toLowerCase().includes(term) ||
          t.description.toLowerCase().includes(term) ||
          t.gatewayTransactionId.toLowerCase().includes(term)
        );
        console.log(`🔍 After search filter (${filters.searchTerm}): ${beforeCount} → ${transactions.length}`);
      }
      
      console.log('✅ Final filtered transactions:', transactions.length);
      return transactions;
    } catch (error) {
      console.error('❌ Error searching transactions:', error);
      return [];
    }
  }

  /**
   * Get date range for time period filter
   */
  getDateRangeForPeriod(period) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    console.log('📅 Calculating date range for period:', period);
    console.log('📅 Current date:', now);
    console.log('📅 Today start:', today);
    
    switch (period) {
      case 'today':
        const todayEnd = new Date(today);
        todayEnd.setHours(23, 59, 59, 999); // End of today
        const todayRange = {
          start: today,
          end: todayEnd
        };
        console.log('📅 Today range:', todayRange);
        return todayRange;
        
      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay()); // Start of current week (Sunday)
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999); // End of current week
        const weekRange = {
          start: weekStart,
          end: weekEnd
        };
        console.log('📅 Week range:', weekRange);
        return weekRange;
        
      case 'month':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
        const monthRange = {
          start: monthStart,
          end: monthEnd
        };
        console.log('📅 Month range:', monthRange);
        return monthRange;
        
      case 'year':
        const yearStart = new Date(today.getFullYear(), 0, 1);
        const yearEnd = new Date(today.getFullYear(), 11, 31, 23, 59, 59, 999);
        const yearRange = {
          start: yearStart,
          end: yearEnd
        };
        console.log('📅 Year range:', yearRange);
        return yearRange;
        
      case 'lifetime':
        // Return null to indicate no date filtering (show all transactions)
        console.log('📅 Lifetime - no date filtering');
        return null;
        
      default:
        console.log('📅 Default - no date filtering');
        return null;
    }
  }

  /**
   * Clear all transaction data (for testing)
   */
  clearAllTransactionData() {
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.storagePrefix)) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log('🗑️ Cleared all transaction data');
      return true;
    } catch (error) {
      console.error('Error clearing transaction data:', error);
      return false;
    }
  }
}

// Create singleton instance
const transactionService = new TransactionService();
export default transactionService;